import { Children, type MouseEvent, type ReactNode, useId, useLayoutEffect, useRef } from 'react';
import { LinienSymbol } from '../ui/linien-symbol.js';
import stile from './auswahl-leiste.module.css';
import pille from './symbol-pille.module.css';

export interface AuswahlLeisteProps {
  position: { oben: number; links: number } | null;
  kommentieren: { erlaubt: true } | { erlaubt: false; grund: string } | null;
  entfernenText: string | null;
  onKommentieren(): void;
  onEntfernen(): void;
  children?: ReactNode;
}

/**
 * Linke Kante der Leiste im Rahmen (M3): nie links hinaus und nie rechts ueber den Rahmen, wenn
 * sie hineinpasst. Ist sie breiter als der Rahmen, beginnt sie links. Ohne Rahmenmass (jsdom,
 * noch nicht gelayoutet) bleibt es bei der gemessenen Stelle.
 */
export function klemmeLinks(links: number, eigeneBreite: number, rahmenBreite: number): number {
  if (rahmenBreite <= 0) return Math.max(0, links);
  return Math.max(0, Math.min(links, rahmenBreite - eigeneBreite));
}

/**
 * Der Druck auf einen Leistenknopf darf die Auswahl, auf die er wirkt, nicht selbst aufheben: Die
 * Seite prueft beim Klick, ob genau die gemeldete Auswahl noch sichtbar steht (H2, Codex-Runde 2).
 * Ohne `preventDefault` setzt ein `mousedown` je nach Browser Fokus und Auswahl um.
 */
function behalteAuswahl(ereignis: MouseEvent<HTMLButtonElement>) {
  ereignis.preventDefault();
}

export function AuswahlLeiste({
  position,
  kommentieren,
  entfernenText,
  onKommentieren,
  onEntfernen,
  children,
}: AuswahlLeisteProps) {
  const grundId = useId();
  const element = useRef<HTMLDivElement>(null);
  const gesperrt = kommentieren !== null && !kommentieren.erlaubt;
  const mitSymbolen = kommentieren !== null || entfernenText !== null;
  // Das Kommentarfeld und Meldungen stehen in einer Karte unter der Pille. `children` kann aus
  // lauter `null` bestehen (keine Meldung) -- dann keine leere Karte.
  const mitInhalt = Children.toArray(children).length > 0;
  // Ihre Breite kennt die Leiste erst nach dem Layout, und sie aendert sich mit dem Inhalt (etwa
  // mit dem Kommentarfeld). Deshalb nach jedem Render vor dem Malen: `left` aus dem Stil unten
  // gegebenenfalls nach links ruecken (M3). Gemessen wird bei `left: 0`: Absolut gesetzt schrumpft
  // die Leiste sonst auf den Platz rechts der Stelle und bricht um (im Browser belegt), statt
  // ueberzustehen -- ihre eigentliche Breite zeigte sich erst gar nicht. React setzt `left` nur
  // neu, wenn sich `position` aendert, und dann laeuft dieser Effekt ohnehin erneut.
  useLayoutEffect(() => {
    const el = element.current;
    if (!el || !position) return;
    const rahmen = el.offsetParent;
    const rahmenBreite = rahmen instanceof HTMLElement ? rahmen.clientWidth : 0;
    el.style.left = '0px';
    el.style.left = `${klemmeLinks(position.links, el.offsetWidth, rahmenBreite)}px`;
  });
  return (
    <div
      ref={element}
      className={stile.leiste}
      role="toolbar"
      aria-label="Auswahl"
      // Gemessene Laufzeitkoordinaten der Auswahl, keine Gestaltungswerte.
      style={position ? { top: `${position.oben}px`, left: `${position.links}px` } : undefined}
    >
      {/* Nur Symbole nach Vorbild Google Docs (Karte j3gkZH2Hgc6K57Oa8f1F). Den Namen traegt das
          `aria-label`, den Tooltip `title` -- beide gleich, damit Maus und Screenreader dasselbe
          hoeren. */}
      {mitSymbolen && (
        <div className={pille.pille}>
          {kommentieren && (
            <button
              type="button"
              className={pille.knopf}
              aria-label="Kommentieren"
              title="Kommentieren"
              disabled={gesperrt}
              aria-describedby={gesperrt ? grundId : undefined}
              onMouseDown={behalteAuswahl}
              onClick={onKommentieren}
            >
              <LinienSymbol name="message" />
            </button>
          )}
          {entfernenText && (
            <button
              type="button"
              className={pille.knopf}
              aria-label={entfernenText}
              title={entfernenText}
              onMouseDown={behalteAuswahl}
              onClick={onEntfernen}
            >
              <LinienSymbol name="trash" />
            </button>
          )}
        </div>
      )}
      {kommentieren && !kommentieren.erlaubt && (
        <p id={grundId} className={`${stile.karte} ${stile.grund}`}>
          {kommentieren.grund}
        </p>
      )}
      {mitInhalt && <div className={stile.karte}>{children}</div>}
    </div>
  );
}
